import { CollectionKey } from '../models/domain.models';

export interface EntityColumnConfig {
  key: string;
  label: string;
}

export interface EntityFieldConfig {
  key: string;
  label: string;
  type: 'text' | 'number' | 'date' | 'time' | 'select';
  required?: boolean;
}

export interface EntityConfig {
  key: CollectionKey;
  title: string;
  addLabelKey: string;
  statuses?: string[];
  icon: string;
  showInMenu?: boolean;
  columns: EntityColumnConfig[];
  createFields: EntityFieldConfig[];
}

export const ENTITY_CONFIGS: EntityConfig[] = [
  {
    key: 'students',
    title: 'entity.students',
    addLabelKey: 'entityAdd.students',
    statuses: ['active', 'disabled'],
    icon: 'school',
    columns: [
      { key: 'fullName', label: 'common.student' },
      { key: 'personalIdentificationNumberMasked', label: 'field.personalIdentificationNumber' },
      { key: 'courseProgress', label: 'resourceDetails.courseProgress' },
      { key: 'paymentStatus', label: 'field.paymentStatus' },
      { key: 'status', label: 'field.status' }
    ],
    createFields: [
      { key: 'name', label: 'field.name', type: 'text', required: true },
      { key: 'surname', label: 'field.surname', type: 'text', required: true },
      { key: 'personalIdentificationNumber', label: 'field.personalIdentificationNumber', type: 'text', required: false },
      { key: 'birthDate', label: 'field.birthDate', type: 'date', required: false },
      { key: 'email', label: 'field.email', type: 'text', required: true },
      { key: 'mobile', label: 'field.mobile', type: 'text', required: true },
      { key: 'streetName', label: 'field.streetName', type: 'text', required: false },
      { key: 'buildingNumber', label: 'field.buildingNumber', type: 'text', required: false },
      { key: 'postalCode', label: 'field.postalCode', type: 'text', required: false },
      { key: 'city', label: 'field.city', type: 'text', required: true },
      { key: 'country', label: 'field.country', type: 'select', required: true }
    ]
  },
  {
    key: 'instructors',
    title: 'entity.instructors',
    addLabelKey: 'entityAdd.instructors',
    statuses: ['active', 'disabled', 'suspended'],
    icon: 'person',
    columns: [
      { key: 'fullName', label: 'common.instructor' },
      { key: 'licenceNumber', label: 'field.licenceNumber' },
      { key: 'monthlyLessonHours', label: 'field.monthlyLessonHours' },
      { key: 'status', label: 'field.status' }
    ],
    createFields: [
      { key: 'name', label: 'field.name', type: 'text', required: true },
      { key: 'surname', label: 'field.surname', type: 'text', required: true },
      { key: 'licenceNumber', label: 'field.licenceNumber', type: 'text', required: true },
      { key: 'licenceCategories', label: 'field.licenceCategories', type: 'select', required: true },
      { key: 'email', label: 'field.email', type: 'text', required: true },
      { key: 'mobile', label: 'field.mobile', type: 'text', required: true },
      { key: 'streetName', label: 'field.streetName', type: 'text', required: false },
      { key: 'buildingNumber', label: 'field.buildingNumber', type: 'text', required: false },
      { key: 'postalCode', label: 'field.postalCode', type: 'text', required: false },
      { key: 'city', label: 'field.city', type: 'text', required: true },
      { key: 'country', label: 'field.country', type: 'select', required: true }
    ]
  },
  {
    key: 'resources',
    title: 'entity.resources',
    addLabelKey: 'entityAdd.resources',
    icon: 'directions_car',
    columns: [
      { key: 'name', label: 'field.resourceName' },
      { key: 'registration', label: 'field.registration' },
      { key: 'categories', label: 'field.categories' },
      { key: 'type', label: 'field.type' },
      { key: 'subType', label: 'field.subType' }
    ],
    createFields: [
      { key: 'name', label: 'field.resourceName', type: 'text', required: true },
      { key: 'registration', label: 'field.registration', type: 'text', required: true },
      { key: 'categories', label: 'field.categories', type: 'select', required: true },
      { key: 'type', label: 'field.type', type: 'select', required: true },
      { key: 'subType', label: 'field.subType', type: 'select', required: true },
      { key: 'nextServiceDate', label: 'field.nextServiceDate', type: 'date' },
      { key: 'issuanceExpiryDate', label: 'field.issuanceExpiryDate', type: 'date' }
    ]
  },
  {
    key: 'courseInstances',
    title: 'entity.courseInstances',
    addLabelKey: 'entityAdd.courseInstances',
    statuses: ['active', 'finished'],
    icon: 'event',
    columns: [
      { key: 'name', label: 'field.courseName' },
      { key: 'courseId', label: 'common.product' },
      { key: 'courseType', label: 'root.products.courseType' },
      { key: 'billingModel', label: 'field.billingModel' },
      { key: 'price', label: 'field.price' },
      { key: 'studentHourlyPrice', label: 'field.studentHourlyPrice' },
      { key: 'dateStarted', label: 'field.dateStarted' },
      { key: 'dateCompleted', label: 'field.dateCompleted' },
      { key: 'status', label: 'field.status' }
    ],
    createFields: [
      { key: 'name', label: 'field.courseName', type: 'text', required: true },
      { key: 'courseId', label: 'common.product', type: 'select', required: true },
      { key: 'billingModel', label: 'field.billingModel', type: 'select', required: true },
      { key: 'price', label: 'field.price', type: 'number' },
      { key: 'studentHourlyPrice', label: 'field.studentHourlyPrice', type: 'number' },
      { key: 'dateStarted', label: 'field.dateStarted', type: 'date', required: true }
    ]
  },
  {
    key: 'courses',
    title: 'entity.courses',
    addLabelKey: 'entityAdd.courses',
    statuses: ['active', 'disabled'],
    icon: 'menu_book',
    showInMenu: false,
    columns: [
      { key: 'name', label: 'field.courseName' },
      { key: 'categories', label: 'field.categories' },
      { key: 'courseType', label: 'root.products.courseType' },
      { key: 'theoryHours', label: 'root.products.theoryHours' },
      { key: 'practiceHours', label: 'root.products.practiceHours' },
      { key: 'status', label: 'field.status' }
    ],
    createFields: [
      { key: 'name', label: 'field.courseName', type: 'text', required: true },
      { key: 'courseType', label: 'root.products.courseType', type: 'select', required: true },
      { key: 'categories', label: 'field.categories', type: 'select', required: true },
      { key: 'theoryHours', label: 'root.products.theoryHours', type: 'number', required: true },
      { key: 'practiceHours', label: 'root.products.practiceHours', type: 'number', required: true }
    ]
  },
  {
    key: 'categories',
    title: 'entity.categories',
    addLabelKey: 'entityAdd.categories',
    statuses: ['active', 'disabled'],
    icon: 'category',
    showInMenu: false,
    columns: [
      { key: 'name', label: 'field.name' },
      { key: 'status', label: 'field.status' }
    ],
    createFields: [{ key: 'name', label: 'field.name', type: 'select', required: true }]
  },
  {
    key: 'organisations',
    title: 'entity.organisations',
    addLabelKey: 'entityAdd.organisations',
    statuses: ['active', 'disabled'],
    icon: 'business',
    showInMenu: false,
    columns: [
      { key: 'name', label: 'field.name' },
      { key: 'addressId', label: 'entityAdd.addresses' },
      { key: 'contactId', label: 'entityAdd.contacts' },
      { key: 'status', label: 'field.status' }
    ],
    createFields: [{ key: 'name', label: 'field.name', type: 'text', required: true }]
  },
  {
    key: 'contacts',
    title: 'entity.contacts',
    addLabelKey: 'entityAdd.contacts',
    statuses: ['active', 'disabled'],
    icon: 'contact_phone',
    showInMenu: false,
    columns: [
      { key: 'email', label: 'field.email' },
      { key: 'mobile', label: 'field.mobile' },
      { key: 'status', label: 'field.status' }
    ],
    createFields: [
      { key: 'email', label: 'field.email', type: 'text', required: true },
      { key: 'mobile', label: 'field.mobile', type: 'text', required: true }
    ]
  },
  {
    key: 'addresses',
    title: 'entity.addresses',
    addLabelKey: 'entityAdd.addresses',
    statuses: ['active', 'disabled'],
    icon: 'home',
    showInMenu: false,
    columns: [
      { key: 'streetName', label: 'field.streetName' },
      { key: 'city', label: 'field.city' },
      { key: 'country', label: 'field.country' },
      { key: 'status', label: 'field.status' }
    ],
    createFields: [
      { key: 'streetName', label: 'field.streetName', type: 'text', required: true },
      { key: 'city', label: 'field.city', type: 'text', required: true },
      { key: 'country', label: 'field.country', type: 'text', required: true }
    ]
  },
  {
    key: 'payments',
    title: 'entity.payments',
    addLabelKey: 'entityAdd.payments',
    statuses: ['paid', 'pendingSettlement'],
    icon: 'payments',
    columns: [
      { key: 'studentId', label: 'common.student' },
      { key: 'courseInstanceId', label: 'common.course' },
      { key: 'amount', label: 'field.amount' },
      { key: 'paymentDate', label: 'field.paymentDate' },
      { key: 'paymentType', label: 'field.paymentType' },
      { key: 'status', label: 'field.status' }
    ],
    createFields: [
      { key: 'studentId', label: 'common.student', type: 'select', required: true },
      { key: 'courseInstanceId', label: 'common.course', type: 'select', required: true },
      { key: 'amount', label: 'field.amount', type: 'number', required: true },
      { key: 'paymentDate', label: 'field.paymentDate', type: 'date', required: true },
      { key: 'paidTo', label: 'field.paidTo', type: 'text', required: true },
      { key: 'paymentType', label: 'field.paymentType', type: 'select', required: true },
      { key: 'additionalInformation', label: 'field.additionalInformation', type: 'text', required: false },
      { key: 'status', label: 'field.status', type: 'select', required: true }
    ]
  },
  {
    key: 'lessons',
    title: 'entity.lessons',
    addLabelKey: 'entityAdd.lessons',
    statuses: ['planned', 'completed', 'cancelled'],
    icon: 'schedule',
    columns: [
      { key: 'studentId', label: 'common.student' },
      { key: 'instructorId', label: 'common.instructor' },
      { key: 'executionDate', label: 'field.executionDate' },
      { key: 'lessonTime', label: 'field.lessonTime' },
      { key: 'durationMinutes', label: 'common.duration' },
      { key: 'status', label: 'field.status' }
    ],
    createFields: [
      { key: 'studentId', label: 'common.student', type: 'select', required: true },
      { key: 'instructorId', label: 'common.instructor', type: 'select', required: true },
      { key: 'courseInstanceId', label: 'common.course', type: 'select', required: true },
      { key: 'resourceId', label: 'common.resource', type: 'select', required: true },
      { key: 'executionDate', label: 'field.executionDate', type: 'date', required: true },
      { key: 'lessonTime', label: 'field.lessonTime', type: 'time', required: true },
      { key: 'durationHours', label: 'lessonDialog.durationHours', type: 'number', required: true }
    ]
  }
];

export function getEntityConfig(key: string | null | undefined): EntityConfig | undefined {
  return ENTITY_CONFIGS.find((item) => item.key === key);
}
